import { Link } from 'react-router-dom'
import LegalPageLayout from '../components/legal/LegalPageLayout'

const LAST_UPDATED = 'December 26, 2024'

const SECTIONS = [
  { id: 'summary', title: 'Summary' },
  { id: 'who-we-are', title: 'Who We Are' },
  { id: 'what-we-collect', title: 'What We Collect' },
  { id: 'how-we-use', title: 'How We Use Your Data' },
  { id: 'third-parties', title: 'Third-Party Services' },
  { id: 'what-we-dont-do', title: 'What We Don\'t Do' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'cookies', title: 'Cookies' },
  { id: 'data-retention', title: 'Data Retention' },
  { id: 'children', title: 'Children\'s Privacy' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' }
]

export default function PolicyPrivacy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
    >
      {/* Summary */}
      <section id="summary">
        <div className="not-prose mb-6 p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
          <h2 className="text-base font-semibold text-neutral-900 mb-4">
            Summary
          </h2>
          <div className="space-y-3 text-[15px] text-neutral-600">
            <p>
              <span className="font-medium text-neutral-800">What we collect:</span>{' '}
              Your email, a securely hashed password, and the content you create (tasks, goals, notes).
            </p>
            <p>
              <span className="font-medium text-neutral-800">What we don't do:</span>{' '}
              Sell your data, track you across the web, run advertising analytics, or share anything with advertisers.
            </p>
            <p>
              <span className="font-medium text-neutral-800">Error monitoring:</span>{' '}
              Our self-hosted OpenObserve service records browser/device info and masked session recordings to help us fix bugs. All text and inputs are masked, so it never receives your personal data or content — and the data stays on our own infrastructure.
            </p>
            <p>
              <span className="font-medium text-neutral-800">Your rights:</span>{' '}
              Access, export, or delete your data at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section id="who-we-are">
        <h2>Who We Are</h2>
        <p>
          Taskrdo is operated by Daniel Knight. We're a personal productivity app built for individuals
          who want to get things done without corporate productivity theater.
        </p>
        <p>
          <strong>Contact:</strong> <a href="mailto:support@taskrdo.com">support@taskrdo.com</a>
        </p>
      </section>

      {/* What We Collect */}
      <section id="what-we-collect">
        <h2>Information We Collect</h2>
        <p>We only collect what's necessary to provide you with a functional task management app:</p>

        <h3>Account Information</h3>
        <ul>
          <li><strong>Email address</strong> - Used for authentication and account recovery</li>
          <li><strong>Password</strong> - Stored securely using industry-standard hashing (we never see your actual password)</li>
          <li><strong>Full name</strong> - Optional, used for personalization</li>
        </ul>

        <h3>Your Content</h3>
        <ul>
          <li><strong>Tasks, goals, and notes</strong> - The productivity content you create</li>
          <li><strong>Projects and labels</strong> - Organizational structures you set up</li>
          <li><strong>Settings and preferences</strong> - Your timezone, theme, and app configuration</li>
        </ul>

        <h3>Technical Data (Error Monitoring)</h3>
        <p>
          Our self-hosted error monitoring service (OpenObserve) automatically collects:
        </p>
        <ul>
          <li>Browser type and version</li>
          <li>Operating system</li>
          <li>Screen size and viewport</li>
          <li>Error stack traces and timestamps</li>
          <li>Page URL and in-app navigation</li>
          <li>Masked session recordings (all text and form inputs are masked)</li>
        </ul>
        <p>
          <strong>Important:</strong> Session recordings mask all text and inputs, so your personal information,
          tasks, goals, and content are never captured. This data is used only to diagnose and fix bugs, and it
          is stored on our own infrastructure — not shared with any third-party monitoring company.
        </p>
      </section>

      {/* How We Use Your Data */}
      <section id="how-we-use">
        <h2>How We Use Your Information</h2>
        <p>Your data is used exclusively to provide and improve the Taskrdo service:</p>
        <ul>
          <li><strong>Authentication</strong> - To log you in and keep your account secure</li>
          <li><strong>Service delivery</strong> - To display your tasks, goals, and productivity data</li>
          <li><strong>Account recovery</strong> - To help you reset your password if needed</li>
          <li><strong>Bug fixes</strong> - To identify and fix application errors (via self-hosted OpenObserve)</li>
        </ul>
        <p>
          That's it. We don't analyze your productivity patterns, we don't build profiles about you,
          and we don't use your data for anything other than running the app.
        </p>
      </section>

      {/* Third-Party Services */}
      <section id="third-parties">
        <h2>Third-Party Services</h2>
        <p>We use a minimal number of third-party services to operate Taskrdo:</p>

        <h3>Supabase (Database & Authentication)</h3>
        <p>
          Your account data and content are stored on Supabase servers in the US East (Virginia) region.
          Supabase provides our authentication system and database infrastructure.
        </p>
        <ul>
          <li>Data is encrypted at rest (AES-256) and in transit (TLS)</li>
          <li>Row-level security ensures you can only access your own data</li>
          <li>
            <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">
              View Supabase's Privacy Policy
            </a>
          </li>
        </ul>

        <h3>OpenObserve (Error Monitoring — Self-Hosted)</h3>
        <p>
          Error monitoring and session replay run on <strong>OpenObserve, which we host ourselves</strong> —
          this data is never sent to an outside monitoring company. We've configured it with privacy in mind:
        </p>
        <ul>
          <li>Session recordings mask all text and form inputs (<code>defaultPrivacyLevel: 'mask'</code>)</li>
          <li>Only technical data (browser, OS, error details, masked recordings) is collected</li>
          <li>Your tasks, goals, and content are never captured</li>
          <li>All telemetry stays on infrastructure we operate</li>
        </ul>
      </section>

      {/* What We Don't Do */}
      <section id="what-we-dont-do">
        <h2>What We Don't Do</h2>
        <p>
          We believe in data minimization. Here's what we <strong>never</strong> do with your information:
        </p>
        <ul>
          <li><strong>We don't sell your data</strong> - Not to advertisers, data brokers, or anyone else</li>
          <li><strong>We don't share your data</strong> - Your content stays between you and our servers</li>
          <li><strong>We don't track you</strong> - No analytics, no behavioral tracking, no user profiling</li>
          <li><strong>We don't use your data for marketing</strong> - No promotional emails based on your content</li>
          <li><strong>We don't monetize your data</strong> - Your productivity data isn't our product</li>
          <li><strong>We don't use tracking cookies</strong> - Only essential authentication cookies</li>
          <li><strong>We don't serve ads</strong> - No advertisements, no ad targeting</li>
        </ul>
        <p>
          <strong>California residents:</strong> We do not sell or share personal information as
          defined by the California Consumer Privacy Act (CCPA).
        </p>
      </section>

      {/* Your Rights */}
      <section id="your-rights">
        <h2>Your Rights</h2>
        <p>You have full control over your data. Here's what you can do:</p>

        <h3>Access Your Data</h3>
        <p>
          You can access all your data directly within the app. Everything you create is visible to you.
        </p>

        <h3>Export Your Data</h3>
        <p>
          You have the right to export your data in a portable format. Contact us at{' '}
          <a href="mailto:support@taskrdo.com">support@taskrdo.com</a> to request an export.
        </p>

        <h3>Delete Your Data</h3>
        <p>
          You can delete your account and all associated data at any time. When you delete your account:
        </p>
        <ul>
          <li>All your tasks, goals, projects, and content are permanently deleted</li>
          <li>Your account information is removed from our database</li>
          <li>This deletion is completed within 30 days</li>
        </ul>

        <h3>Correct Your Data</h3>
        <p>
          You can update your profile information, email, and password directly in the app settings.
        </p>

        <h3>Additional Rights (EU/UK Residents)</h3>
        <p>
          Under GDPR, you also have the right to restrict processing, object to processing, and
          lodge a complaint with your local data protection authority.
        </p>
      </section>

      {/* Data Security */}
      <section id="data-security">
        <h2>Data Security</h2>
        <p>We take the security of your data seriously:</p>
        <ul>
          <li><strong>Password hashing</strong> - Your password is never stored in plain text</li>
          <li><strong>Encryption in transit</strong> - All data is transmitted over HTTPS/TLS</li>
          <li><strong>Encryption at rest</strong> - Your data is encrypted on our database servers (AES-256)</li>
          <li><strong>Row-level security</strong> - Database policies ensure you can only access your own data</li>
          <li><strong>Multi-factor authentication</strong> - Optional MFA is available for enhanced account security</li>
          <li><strong>Session management</strong> - Automatic token refresh and secure session handling</li>
        </ul>
      </section>

      {/* Cookies */}
      <section id="cookies">
        <h2>Cookies</h2>
        <p>
          We use only essential cookies necessary for the app to function. These are strictly
          functional cookies used for:
        </p>
        <ul>
          <li><strong>Authentication</strong> - Keeping you logged in across sessions</li>
          <li><strong>Session management</strong> - Maintaining your active session</li>
          <li><strong>Security</strong> - Protecting against cross-site request forgery</li>
        </ul>
        <p>
          We do not use:
        </p>
        <ul>
          <li>Tracking cookies</li>
          <li>Analytics cookies</li>
          <li>Advertising cookies</li>
          <li>Third-party cookies for marketing</li>
        </ul>
      </section>

      {/* Data Retention */}
      <section id="data-retention">
        <h2>Data Retention</h2>
        <p>Here's how long we keep your data:</p>
        <ul>
          <li><strong>Account data</strong> - Retained until you delete your account</li>
          <li><strong>Your content (tasks, goals, etc.)</strong> - Retained until you delete it or your account</li>
          <li><strong>Error logs &amp; masked session replays (self-hosted OpenObserve)</strong> - Retained for debugging per our instance's retention policy</li>
          <li><strong>Deleted data</strong> - Permanently removed within 30 days of deletion request</li>
        </ul>
      </section>

      {/* Children's Privacy */}
      <section id="children">
        <h2>Children's Privacy</h2>
        <p>
          Taskrdo is not directed to children under 13. We do not knowingly collect personal
          information from children under 13. If you believe we have collected information from
          a child under 13, please contact us immediately at{' '}
          <a href="mailto:support@taskrdo.com">support@taskrdo.com</a>, and we will delete
          the information.
        </p>
      </section>

      {/* Changes to This Policy */}
      <section id="changes">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make changes:
        </p>
        <ul>
          <li>We'll update the "Last updated" date at the top of this page</li>
          <li>For significant changes, we'll notify you via email or in-app notification</li>
          <li>Continued use of the service after changes constitutes acceptance</li>
        </ul>
        <p>
          We review this policy at least annually to ensure it remains accurate and up to date.
        </p>
      </section>

      {/* Contact Us */}
      <section id="contact">
        <h2>Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or your data, we're here to help:
        </p>
        <ul>
          <li>
            <strong>Email:</strong> <a href="mailto:support@taskrdo.com">support@taskrdo.com</a>
          </li>
        </ul>
        <p>
          We aim to respond to all privacy-related inquiries within 30 days.
        </p>
      </section>

      {/* Final Note */}
      <div className="not-prose mt-12 p-6 rounded-xl bg-surface-secondary border border-border">
        <p className="text-text-secondary text-sm">
          <strong className="text-text-primary">Our commitment:</strong> Taskrdo is built for you,
          not for data harvesting. We believe your productivity data is personal, and we treat it that
          way. If you ever have concerns about how we handle your information, please reach out.
          We're real humans, and we care about doing the right thing.
        </p>
      </div>
    </LegalPageLayout>
  )
}
